import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const PAGE_SIZE = 20;

// Activity from people you follow. Modelled on useNotifications, with two
// deliberate differences:
//
//   • Keyset cursor, not offset. get_following_activity merges posts + approved
//     videos, and a following feed churns as people post — offset pagination
//     duplicates and skips rows at page boundaries. useNotifications gets away
//     with offset only because notification arrival is slow.
//   • No markRead / markAllRead. This tab is unbadged by design (see 027's
//     header): it's a browsable activity list, not a notification queue.
//
// `enabled` supports lazy-mounting from a tab: hooks can't be conditional, so
// the flag is checked INSIDE the effect to keep hook order stable across
// renders. Landing on the "You" tab must not cost an extra RPC.
export function useFollowingActivity(enabled = true) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(false);

  // cursor === null fetches the first page
  const fetchPage = useCallback(async (cursor) => {
    const { data, error: rpcError } = await supabase.rpc('get_following_activity', {
      p_limit: PAGE_SIZE,
      p_before: cursor?.at ?? null,
      p_before_id: cursor?.id ?? null,
    });

    if (rpcError) {
      console.error('useFollowingActivity:', rpcError);
      return null;
    }

    return data ?? [];
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    (async () => {
      const data = await fetchPage(null);
      if (cancelled) return;
      if (data === null) {
        setError(true);
      } else {
        setItems(data);
        setHasMore(data.length === PAGE_SIZE);
      }
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [enabled, fetchPage]);

  const fetchMore = useCallback(async () => {
    if (loadingMore || !hasMore || items.length === 0) return;
    setLoadingMore(true);

    // Cursor is the last row's (activity_at, item_id) — must match the RPC's
    // ORDER BY tiebreak or rows straddling a shared timestamp get skipped.
    const last = items[items.length - 1];
    const data = await fetchPage({ at: last.activity_at, id: last.item_id });
    if (data !== null) {
      setItems(prev => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    }
    setLoadingMore(false);
  }, [fetchPage, items, loadingMore, hasMore]);

  return { items, loading, loadingMore, hasMore, error, fetchMore };
}
